// src/components/games/crossroads/ManaPoolDisplay.tsx
// Visual display of available mana pool with color coding

import React from 'react';
import { IonCard, IonCardContent, IonText, IonBadge, IonIcon } from '@ionic/react';
import { waterOutline, flameOutline, flashOutline } from 'ionicons/icons';
import { ElementType, BattlefieldManaSource } from './types';
import './ManaPoolDisplay.css';

interface ManaPoolDisplayProps {
  manaPool: { [key in ElementType]?: number };
  manaSources: BattlefieldManaSource[];
  compact?: boolean;
}

const ELEMENT_COLORS: { [key in ElementType]: string } = {
  fire: '#ff4444',
  water: '#4488ff',
  earth: '#44bb44',
  dark: '#9944ff',
  light: '#ffdd44',
  fae: '#ff88ff',
  wind: '#88ddff',
  neutral: '#999999',
};

const ELEMENT_ICONS: { [key in ElementType]: string } = {
  fire: '🔥',
  water: '💧',
  earth: '🌱',
  dark: '🌑',
  light: '☀️',
  fae: '✨',
  wind: '💨',
  neutral: '⚪',
};

export const ManaPoolDisplay: React.FC<ManaPoolDisplayProps> = ({
  manaPool,
  manaSources,
  compact = false,
}) => {
  // Calculate available vs tapped mana
  const totalSources = manaSources.length;
  const tappedSources = manaSources.filter(m => m.isTapped).length;
  const untappedSources = totalSources - tappedSources;

  // Get elements with available mana
  const availableElements = Object.entries(manaPool)
    .filter(([_, amount]) => amount && amount > 0)
    .sort(([_, a], [__, b]) => (b || 0) - (a || 0)); // Sort by amount descending

  if (compact) {
    return (
      <div className="mana-pool-compact">
        {availableElements.map(([element, amount]) => (
          <div
            key={element}
            className="mana-orb"
            style={{
              background: `linear-gradient(135deg, ${ELEMENT_COLORS[element as ElementType]}, ${ELEMENT_COLORS[element as ElementType]}dd)`,
            }}
          >
            <span className="mana-orb-icon">{ELEMENT_ICONS[element as ElementType]}</span>
            <span className="mana-orb-amount">{amount}</span>
          </div>
        ))}
        {availableElements.length === 0 && (
          <IonText color="medium">
            <small>No mana available</small>
          </IonText>
        )}
      </div>
    );
  }

  return (
    <IonCard className="mana-pool-card">
      <IonCardContent>
        <div className="mana-pool-header">
          <IonText>
            <h3 style={{ margin: 0, fontSize: '1em', fontWeight: 600 }}>
              <IonIcon icon={flashOutline} style={{ marginRight: '6px' }} />
              Mana Pool
            </h3>
          </IonText>
          <IonBadge color={untappedSources > 0 ? 'success' : 'medium'}>
            {untappedSources}/{totalSources} Sources
          </IonBadge>
        </div>

        {/* Mana by element */}
        <div className="mana-elements">
          {availableElements.length > 0 ? (
            availableElements.map(([element, amount]) => (
              <div key={element} className="mana-element">
                <div
                  className="mana-element-bar"
                  style={{
                    background: `linear-gradient(90deg, ${ELEMENT_COLORS[element as ElementType]}, ${ELEMENT_COLORS[element as ElementType]}aa)`,
                  }}
                >
                  <span className="mana-element-icon">
                    {ELEMENT_ICONS[element as ElementType]}
                  </span>
                  <span className="mana-element-name">
                    {element.charAt(0).toUpperCase() + element.slice(1)}
                  </span>
                  <IonBadge color="light" className="mana-element-amount">
                    {amount}
                  </IonBadge>
                </div>
              </div>
            ))
          ) : (
            <div className="mana-empty">
              <IonText color="medium">
                <p style={{ margin: 0, fontSize: '0.9em' }}>
                  No mana available. Play mana sources to generate mana!
                </p>
              </IonText>
            </div>
          )}
        </div>

        {/* Mana source status */}
        {totalSources > 0 && (
          <div className="mana-sources-status">
            <div className="mana-source-dots">
              {manaSources.map((source, idx) => (
                <div
                  key={source.instanceId}
                  className={`mana-source-dot ${source.isTapped ? 'tapped' : 'untapped'}`}
                  title={source.isTapped ? 'Tapped' : 'Ready'}
                />
              ))}
            </div>
            <IonText color="medium">
              <small>
                {tappedSources > 0 ? `${tappedSources} tapped • ` : ''}
                {untappedSources} ready to tap
              </small>
            </IonText>
          </div>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default ManaPoolDisplay;
